/**
 * Patient Display Component
 *
 * Displays patient avatar, name, DOB, and close button
 */

import React from 'react';
import { Close as CloseIcon, Person as PersonIcon } from '@mui/icons-material';
import { Avatar, Box, IconButton, Link, Typography } from '@mui/material';
import type { Patient } from '@ts/types';
import { formatPatientName } from '@utils/patientUtils';

const PURPLE_PRIMARY = '#7C3AED';

export interface PatientDisplayProps {
  patient: Patient;
  onClose?: () => void;
  /** Callback when patient name is clicked */
  onPatientClick?: (patientId: string) => void;
}

export const PatientDisplay: React.FC<PatientDisplayProps> = ({
  patient,
  onClose,
  onPatientClick,
}) => {
  const handleNameClick = () => {
    if (onPatientClick && patient.patient_id) {
      onPatientClick(patient.patient_id);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <Avatar
        sx={{
          width: 40,
          height: 40,
          bgcolor: PURPLE_PRIMARY,
          fontSize: '1rem',
          fontWeight: 600,
        }}
      >
        {patient.first_name?.[0]?.toUpperCase() || patient.last_name?.[0]?.toUpperCase() || (
          <PersonIcon />
        )}
      </Avatar>
      <Box>
        {onPatientClick ? (
          <Link
            component="button"
            variant="subtitle1"
            onClick={handleNameClick}
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              lineHeight: 1.2,
              textDecoration: 'none',
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline',
                color: 'primary.main',
              },
            }}
          >
            {formatPatientName(patient.first_name, patient.last_name, patient.preferred_name)}
          </Link>
        ) : (
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              lineHeight: 1.2,
            }}
          >
            {formatPatientName(patient.first_name, patient.last_name, patient.preferred_name)}
          </Typography>
        )}
        {patient.dob && (
          <Typography variant="caption" color="text.secondary" display="block">
            DOB: {patient.dob}
          </Typography>
        )}
      </Box>
      {onClose && (
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            ml: 1,
            color: 'text.secondary',
            '&:hover': {
              color: 'error.main',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};
